import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { css } from "@emotion/css";
import type { HomeState } from "../../types/state";
import { MathMarkdown } from "../../components/MathMarkdown";

const pageStyle = css`
  max-width: 600px;
  margin: 0 auto;
  padding: 24px 16px;
  color: var(--color-text);
`;

const headingRowStyle = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
`;

const headingStyle = css`
  font-size: 1.5em;
  font-weight: 700;
  color: var(--color-text);
`;

// The only in-app route to billing.  Apple guideline 3.1.2 requires a
// subscription bought in-app to be manageable from in-app, and the native
// wrapper also watches for the /settings path -- so this link is what makes
// the payment surfaces reachable at all, not decoration.
const settingsLinkStyle = css`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 0.9em;
  color: var(--color-text-muted);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }
`;

const arcCardStyle = css`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px var(--color-shadow);
`;

const arcTitleStyle = css`
  font-size: 1.15em;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 8px;
`;

const arcDetailStyle = css`
  font-size: 0.9em;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-bottom: 6px;
`;

/* Markdown-aware version of arcDetailStyle: same typography, but
 * with paragraph and list spacing so multi-paragraph arc summaries
 * (saved on activation from the recommendation intro) don't render
 * as a wall of text on the home page. */
const arcSummaryStyle = css`
  font-size: 0.9em;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-bottom: 6px;

  p {
    margin: 0 0 8px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul, ol {
    margin: 0 0 8px 0;
    padding-left: 20px;
  }
  ul:last-child, ol:last-child {
    margin-bottom: 0;
  }
  li {
    margin-bottom: 3px;
  }
  strong, b {
    color: var(--color-text);
  }
`;

const etaBadgeStyle = css`
  display: inline-block;
  font-size: 0.8em;
  padding: 3px 10px;
  background: var(--color-primary-muted);
  color: var(--color-primary);
  border-radius: 12px;
  margin-top: 8px;
`;

const startButtonStyle = css`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 24px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--color-primary-hover);
  }
`;

const recalibrateBtnStyle = css`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 24px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--color-bg-raised);
  }
`;

const sectionTitleStyle = css`
  font-size: 1em;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 12px;
`;

const winItemStyle = css`
  padding: 12px 14px;
  background: var(--color-success-bg);
  border: 1px solid var(--color-success-border);
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 0.9em;
  color: var(--color-success-text);
`;

const emptyStateStyle = css`
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-muted);
`;

const loadingStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--color-text-muted);
`;

const errorStyle = css`
  text-align: center;
  padding: 40px 20px;
  color: var(--color-error);
`;

export const CoachingHome: React.FC = () => {
  const navigate = useNavigate();
  const [homeState, setHomeState] = useState<HomeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/coaching/home", { credentials: "include" })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setError(`Failed to load (${res.status})`);
          setLoading(false);
          return;
        }
        const data = await res.json();
        setHomeState(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Coaching home fetch error:", err);
        setError("Failed to connect to server");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleStartSession = useCallback(() => {
    if (!homeState?.activeArc) return;
    navigate(
      `/coaching/session?arcId=${homeState.activeArc.arcId}&launchMode=regular`
    );
  }, [homeState, navigate]);

  const handleRecalibrate = useCallback(() => {
    navigate("/arc-generation?reason=recalibration");
  }, [navigate]);

  const handleStartArcGeneration = useCallback(() => {
    navigate("/arc-generation?reason=onboarding");
  }, [navigate]);

  if (loading) {
    return <div className={loadingStyle}>Loading...</div>;
  }

  if (error) {
    return (
      <div className={errorStyle} data-testid="coaching-home-error">
        {error}
      </div>
    );
  }

  if (!homeState) {
    return <div className={emptyStateStyle}>Unable to load coaching home.</div>;
  }

  return (
    <div className={pageStyle} data-testid="coaching-home">
      <div className={headingRowStyle}>
        <div className={headingStyle}>Coaching</div>
        <button
          className={settingsLinkStyle}
          onClick={() => navigate("/settings")}
          data-testid="coaching-home-settings"
        >
          Settings
        </button>
      </div>

      {homeState.activeArc ? (
        <>
          <div className={arcCardStyle} data-testid="arc-card">
            <div className={arcTitleStyle} data-testid="arc-title">
              {homeState.activeArc.title}
            </div>
            <div className={arcDetailStyle} data-testid="arc-proof">
              {homeState.activeArc.proofStatement}
            </div>
            <div className={arcSummaryStyle} data-testid="arc-summary">
              <MathMarkdown>{homeState.activeArc.summary || ""}</MathMarkdown>
            </div>
            {homeState.activeArc.etaSessionsRemaining != null && (
              <div className={etaBadgeStyle} data-testid="arc-eta">
                {homeState.activeArc.etaSessionsRemaining} sessions remaining
              </div>
            )}
          </div>

          <button
            className={startButtonStyle}
            onClick={handleStartSession}
            data-testid="start-session-button"
          >
            Start Session
          </button>

          {homeState.showRecalibrate && (
            <button
              className={recalibrateBtnStyle}
              onClick={handleRecalibrate}
              data-testid="recalibrate-button"
            >
              Recalibrate Quest
            </button>
          )}
        </>
      ) : (
        <div className={emptyStateStyle}>
          <p>No active learning Quest.</p>
          <button
            className={startButtonStyle}
            onClick={handleStartArcGeneration}
            data-testid="start-arc-gen-button"
          >
            Set Up Your Learning Quest
          </button>
        </div>
      )}

      {homeState.recentWins?.length > 0 && (
        <>
          <div className={sectionTitleStyle}>Recent Wins</div>
          {homeState.recentWins.map((win) => (
            <div
              key={win.outcomeId}
              className={winItemStyle}
              data-testid="recent-win"
            >
              {win.title}
            </div>
          ))}
        </>
      )}
    </div>
  );
};
